import React, { useEffect, useRef, useState } from 'react';
import {
  approveCampaign, CampaignLang, CampaignText, MarketingCampaign, rejectCampaign, reviewCampaignWithAi,
  revokeCampaignApproval, sendCampaign, updateCampaign
} from '../../../api/services/marketingService';
import { BigButton, ICONS, LineIcon, LinkButton, Segmented, StatusPill } from '../../../components/ui/dg';
import dingoLogo from '../../../assets/img/logo/dingo-logo.png';
import { audienceText, campaignState, channelsText, emptyContent, emptyText, LANG_NAME } from './shared';

/**
 * Relecture d'une campagne, à droite de la liste : le texte par langue
 * (modifiable tant qu'elle n'est pas partie), la relecture IA, qui l'a écrite,
 * et le push tel qu'il s'affichera. En bas, la décision du moment : refuser ou
 * approuver, puis envoyer. La discussion et l'historique restent sur la page
 * complète de la campagne.
 */

const LANG_TABS: { id: CampaignLang; label: string }[] = [
  { id: 'fr', label: 'Français' },
  { id: 'en', label: 'English' },
  { id: 'ar', label: 'العربية' }
];

const field = 'w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 text-[15px] text-[#1E293B] placeholder-[#9CA3AF] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10 read-only:bg-[#FAFAFD]';

interface Props {
  campaign: MarketingCampaign;
  token: string;
  meId: string;
  onChanged: (campaign: MarketingCampaign) => void;
  onOpenFull: () => void;
}

const CampaignReview = ({ campaign, token, meId, onChanged, onOpenFull }: Props) => {
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [content, setContent] = useState<Record<CampaignLang, CampaignText>>(() => ({ ...emptyContent(), ...campaign.content }));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);

  useEffect(() => {
    setContent({ ...emptyContent(), ...campaign.content });
    setDirty(false);
    setRefusing(false);
    setConfirmSend(false);
  }, [campaign.id_campaign, campaign.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setNotice(null); setLang('fr'); }, [campaign.id_campaign]);

  // Comme sur la page complète : l'IA assignée relit chaque version une fois.
  const autoReviewed = useRef<string | null>(null);
  useEffect(() => {
    if (!campaign.ai_reviewer || campaign.ai_review_current) return;
    if (!['pending', 'approved'].includes(campaign.status)) return;
    const version = `${campaign.id_campaign}:${campaign.updated_at}`;
    if (autoReviewed.current === version) return;
    autoReviewed.current = version;
    reviewCampaignWithAi(token, campaign.id_campaign).then(onChanged).catch(() => undefined);
  }, [campaign, token, onChanged]);

  const act = async (fn: () => Promise<MarketingCampaign>, ok: string) => {
    setBusy(true);
    setNotice(null);
    try {
      const updated = await fn();
      if (updated) onChanged(updated);
      setNotice({ ok: true, text: ok });
    } catch (err: any) {
      setNotice({ ok: false, text: err.message || 'Action impossible.' });
    } finally {
      setBusy(false);
    }
  };

  const editable = ['pending', 'approved'].includes(campaign.status);
  const isAuthor = Boolean(campaign.created_by && meId && String(campaign.created_by) === meId);
  const state = campaignState(campaign.status);
  const text = content[lang] || emptyText();
  const rtl = lang === 'ar';
  const fallback = lang !== 'fr' && !(text.title || '').trim();
  const shown = fallback ? content.fr : text;
  const langs = LANG_TABS.filter((l) => l.id === 'fr' || (content[l.id]?.title || '').trim()).map((l) => l.id).join(', ');
  const setField = (key: keyof CampaignText, value: string) => {
    setContent((cur) => ({ ...cur, [lang]: { ...(cur[lang] || emptyText()), [key]: value } }));
    setDirty(true);
  };
  const save = () => act(async () => {
    const wasApproved = campaign.status === 'approved';
    const updated = await updateCampaign(token, campaign.id_campaign, { content });
    if (wasApproved && updated.status === 'pending') setTimeout(() => setNotice({ ok: true, text: 'Enregistré. L’approbation est retirée, il faut de nouveau approuver.' }));
    return updated;
  }, 'Modifications enregistrées.');

  const review = campaign.ai_review;
  const reviewCurrent = Boolean(review && campaign.ai_review_current);
  const issues = review ? Object.entries(review.languages || {}).flatMap(([code, l]) => (l?.issues || []).map((i) => ({ ...i, lang: code as CampaignLang }))) : [];

  const author = campaign.created_by ? (campaign.created_by_name || 'un administrateur') : 'l’API Dingo';
  const authorNote = campaign.status === 'pending'
    ? (isAuthor ? 'Vous l’avez écrite. Une autre personne du staff doit l’approuver avant l’envoi.'
      : `Écrite par ${author}${campaign.created_by ? ', une autre personne du staff' : '. Une personne du staff'} doit l’approuver avant l’envoi.`)
    : campaign.status === 'approved' ? `Écrite par ${author}, approuvée par ${campaign.approved_by_name || 'un administrateur'}. Prête à partir.`
    : campaign.status === 'sent' ? `Envoyée${campaign.stats?.recipients != null ? ` à ${campaign.stats.recipients} comptes` : ''}, ${campaign.stats?.push?.sent ?? 0} push et ${campaign.stats?.email?.sent ?? 0} e-mails.`
    : campaign.status === 'sending' ? 'Envoi en cours.'
    : 'Fermée sans envoi.';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-3 border-b border-[#EEF0F4] px-7 pb-4 pt-5">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[20px] font-extrabold leading-[26px] text-[#1E293B]">{campaign.content?.fr?.title || '(sans titre)'}</h2>
          <p className="mt-0.5 truncate text-[13.5px] text-[#6B7280]">{channelsText(campaign)} · {audienceText(campaign)} · {langs}</p>
        </div>
        <StatusPill tone={state.tone} className="mt-1">{state.label}</StatusPill>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_minmax(300px,0.85fr)]">
        <div className="min-w-0 px-7 py-6">
          <Segmented options={LANG_TABS} value={lang} onChange={(id) => setLang(id as CampaignLang)} ariaLabel="Langue" />
          {fallback && (
            <p className="mt-3 text-[12.5px] text-[#6B7280]">Pas de version en {LANG_NAME[lang]}. Ces comptes recevront le français{editable ? ', ou écrivez-la ici' : ''}.</p>
          )}

          <label className="mb-2 mt-6 block text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]" htmlFor="campaign-title">Titre</label>
          <input id="campaign-title" dir={rtl ? 'rtl' : 'ltr'} value={text.title} maxLength={80} readOnly={!editable}
            placeholder={fallback ? content.fr.title : ''} onChange={(e) => setField('title', e.target.value)}
            className={`${field} h-[52px] font-bold`} />

          <label className="mb-2 mt-5 block text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]" htmlFor="campaign-body">Message</label>
          <textarea id="campaign-body" dir={rtl ? 'rtl' : 'ltr'} value={text.body} rows={4} readOnly={!editable}
            placeholder={fallback ? content.fr.body : ''} onChange={(e) => setField('body', e.target.value)}
            className={`${field} resize-none py-3.5 leading-[1.55]`} />

          {editable && dirty && (
            <div className="mt-3 flex items-center justify-end gap-3">
              {campaign.status === 'approved' && <span className="text-[12.5px] text-[#B45309]">Enregistrer retirera l’approbation.</span>}
              <button type="button" onClick={() => { setContent({ ...emptyContent(), ...campaign.content }); setDirty(false); }} className="text-[13px] font-bold text-[#6B7280] hover:text-[#1E293B]">Annuler</button>
              <button type="button" disabled={busy} onClick={save} className="h-10 rounded-[20px] bg-[#4F46E5] px-4 text-[13.5px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-50">Enregistrer</button>
            </div>
          )}

          <div className="mx-0 mt-6 space-y-3 lg:mx-7">
            {reviewCurrent && review?.status === 'ok' && (
              <div className="flex gap-3 rounded-2xl bg-[#ECFDF5] px-5 py-4">
                <LineIcon d={ICONS.check} size={18} color="#047857" width={2.4} />
                <div>
                  <b className="block text-[14px] font-bold text-[#047857]">Relecture faite</b>
                  <span className="block text-[13px] leading-[1.45] text-[#047857]">Aucune faute en {['fr', 'en', 'ar'].filter((c) => review.languages?.[c as CampaignLang]).map((c) => LANG_NAME[c as CampaignLang]).join(', ').replace(/, ([^,]*)$/, ' et $1') || 'français'}.</span>
                </div>
              </div>
            )}
            {reviewCurrent && review?.status === 'issues' && (
              <div className="rounded-2xl bg-[#FFF8EB] px-5 py-4">
                <b className="block text-[14px] font-bold text-[#B45309]">{issues.length} remarque{issues.length > 1 ? 's' : ''} de la relecture</b>
                <ul className="mt-2 space-y-2">
                  {issues.slice(0, 6).map((i, n) => (
                    <li key={n} className="text-[13px] leading-[1.45] text-[#92400E]">
                      <span className="font-bold uppercase">{i.lang}</span> « {i.excerpt} », {i.problem}. Plutôt « {i.suggestion} ».
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {editable && campaign.ai_reviewer && !reviewCurrent && (
              <div className="rounded-2xl bg-[#F7F7FB] px-5 py-4 text-[13px] text-[#4B5563]">Relecture en cours…</div>
            )}
            {editable && !campaign.ai_reviewer && !reviewCurrent && (
              <div className="flex items-center justify-between gap-3 rounded-2xl bg-[#F7F7FB] px-5 py-4 text-[13px] text-[#4B5563]">
                Pas encore relue.
                <LinkButton disabled={busy} onClick={() => act(() => reviewCampaignWithAi(token, campaign.id_campaign), 'Relecture faite.')}>Faire relire</LinkButton>
              </div>
            )}
            {reviewCurrent && (review?.status === 'unavailable' || review?.status === 'error') && (
              <div className="rounded-2xl bg-[#F7F7FB] px-5 py-4 text-[13px] text-[#4B5563]">Relecture indisponible pour le moment.</div>
            )}
            <div className="rounded-2xl bg-[#F7F7FB] px-5 py-4 text-[13.5px] leading-[1.5] text-[#4B5563]">{authorNote}</div>
            {campaign.note && <div className="rounded-2xl border border-[#EEF0F4] px-5 py-4 text-[13px] leading-[1.5] text-[#4B5563] whitespace-pre-wrap">{campaign.note}</div>}
          </div>
        </div>

        {/* Le push tel qu'il s'affichera sur l'écran verrouillé. */}
        <div className="flex justify-center border-t border-[#EEF0F4] bg-[#FAFAFD] px-7 py-8 lg:border-l lg:border-t-0">
          <div className="w-full max-w-[290px] self-start rounded-[34px] bg-[#1E293B] p-3">
            <div className="flex aspect-[0.82] flex-col items-center rounded-[26px] bg-gradient-to-b from-[#6C63F0] to-[#4F46E5] px-3 pt-10">
              <span className="text-[44px] font-light tracking-[-1px] text-white">09:41</span>
              <div dir={rtl ? 'rtl' : 'ltr'} className="mt-6 flex w-full gap-2.5 rounded-[16px] bg-white/95 p-3 text-left shadow-[0_6px_20px_rgba(15,23,42,0.18)]">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-white shadow-[0_0_0_1px_#EEF0F4]">
                  <img src={dingoLogo} alt="" className="w-6" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2 text-[11px]">
                    <b className="font-bold text-[#1E293B]">Dingo</b>
                    <span className="text-[#6B7280]">{lang === 'ar' ? 'الآن' : lang === 'en' ? 'now' : 'maintenant'}</span>
                  </span>
                  <b className="block text-[12.5px] font-bold leading-[16px] text-[#1E293B]">{shown.title || 'Titre du push'}</b>
                  <span className="mt-0.5 block text-[12px] leading-[16px] text-[#1E293B] line-clamp-4">{shown.body || 'Le message'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {notice && <p className={`mx-7 mb-1 mt-3 rounded-xl px-3 py-2 text-[13px] ${notice.ok ? 'bg-[#ECFDF5] text-[#047857]' : 'bg-[#FDECEF] text-[#B91C1C]'}`}>{notice.text}</p>}

      {refusing && (
        <div className="mx-7 mt-3 flex items-center gap-2">
          <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Pourquoi la refuser ? (facultatif)"
            className="h-11 min-w-0 flex-1 rounded-2xl border border-[#E5E7EB] px-4 text-[14px] focus:border-[#C7C4F6] focus:outline-none" />
          <button type="button" onClick={() => setRefusing(false)} className="px-2 text-[13px] font-bold text-[#6B7280]">Annuler</button>
        </div>
      )}
      {confirmSend && (
        <p className="mx-7 mt-3 rounded-xl bg-[#FDECEF] px-3 py-2 text-[13px] font-semibold text-[#B91C1C]">Envoyer maintenant à {audienceText(campaign)} ? Ça ne se rattrape pas.</p>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-[#EEF0F4] px-7 py-4">
        <LinkButton onClick={onOpenFull}>Discussion et historique{campaign.comments_count ? ` (${campaign.comments_count})` : ''}</LinkButton>
        <span className="flex-1" />
        {campaign.status === 'pending' && (
          <>
            <BigButton kind="danger" disabled={busy} className="min-w-[180px]"
              onClick={() => (refusing ? act(() => rejectCampaign(token, campaign.id_campaign, reason.trim() || undefined), 'Campagne refusée, elle ne partira pas.') : setRefusing(true))}>
              {refusing ? 'Confirmer le refus' : 'Refuser'}
            </BigButton>
            {!refusing && (
              <BigButton disabled={busy || isAuthor || dirty || (campaign.channels || []).length === 0} className="min-w-[230px]"
                onClick={() => act(() => approveCampaign(token, campaign.id_campaign), 'Campagne approuvée.')}>
                <LineIcon d={ICONS.check} size={18} color="#fff" width={2.4} />
                Approuver
              </BigButton>
            )}
          </>
        )}
        {campaign.status === 'approved' && (
          <>
            <BigButton kind="secondary" disabled={busy} onClick={() => (confirmSend ? setConfirmSend(false) : act(() => revokeCampaignApproval(token, campaign.id_campaign), 'Approbation retirée.'))}>
              {confirmSend ? 'Annuler' : 'Retirer l’approbation'}
            </BigButton>
            <BigButton disabled={busy || dirty} className="min-w-[200px]"
              onClick={() => (confirmSend
                ? act(async () => { setConfirmSend(false); const sent = await sendCampaign(token, campaign.id_campaign); if (sent.stats?.error) throw new Error(`Envoi interrompu, ${sent.stats.error}. La campagne reste approuvée.`); return sent; }, 'Campagne envoyée.')
                : setConfirmSend(true))}>
              {confirmSend ? 'Confirmer l’envoi' : 'Envoyer'}
            </BigButton>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignReview;
